import {
  createAsyncThunk,
  createSlice,
  Draft,
  isFulfilled,
  PayloadAction
} from '@reduxjs/toolkit';
import { login, pingCurrentUser, register } from '@ianesthesie-kit/redux';
import { hoursToMillis, StorageAccess } from '@ianesthesie-kit/core';
import { differenceInMilliseconds, isValid } from 'date-fns';
import { RootState } from '@/store';
import { ID } from '@ianesthesie-kit/services';
import { captureThrowOnDev } from '@/util';

const POSTPONE_VERIFICATION_STORAGE_KEY = 'comm_verification_postponed';
const POSTPONE_VERIFICATION_TTL = hoursToMillis(12);
const POSTPONE_PUSH_STORAGE_KEY = 'push_permission_postponed';
const POSTPONE_PUSH_TTL = hoursToMillis(3);

const isPostponedOnLocalStorage = (
  userId: ID | undefined,
  storageKey: string,
  ttlMs: number
): boolean => {
  if (!userId) {
    return false;
  }
  const storage = StorageAccess.getInstance();
  const json = storage.getItem(storageKey);
  if (!json) {
    return false;
  }

  try {
    const { userId: storedUserId, timestamp } = JSON.parse(json);

    if (storedUserId !== userId) {
      return false;
    }

    const date = new Date(+timestamp);
    if (!isValid(date)) {
      storage.removeItem(storageKey);
      return false;
    }
    if (differenceInMilliseconds(Date.now(), date) > ttlMs) {
      storage.removeItem(storageKey);
      return false;
    }

    return true;
  } catch (err) {
    storage.removeItem(storageKey);
    captureThrowOnDev(err);
    return false;
  }
};

export const postponeCommChannelVerification = createAsyncThunk(
  'appUi',
  (arg, thunkAPI) => {
    const userId = (thunkAPI.getState() as RootState).auth.user?.id;
    if (userId) {
      StorageAccess.getInstance().setItem(
        POSTPONE_VERIFICATION_STORAGE_KEY,
        JSON.stringify({
          userId,
          timestamp: Date.now()
        })
      );
    }

    thunkAPI.dispatch(
      uiSlice.actions.setCommChannelVerificationPostponed(true)
    );
  }
);

export const postponePushPermission = createAsyncThunk(
  'appUi',
  (arg, thunkAPI) => {
    const userId = (thunkAPI.getState() as RootState).auth.user?.id;
    if (userId) {
      StorageAccess.getInstance().setItem(
        POSTPONE_PUSH_STORAGE_KEY,
        JSON.stringify({
          userId,
          timestamp: Date.now()
        })
      );
    }

    thunkAPI.dispatch(uiSlice.actions.setPushPermissionPostponed(true));
  }
);

interface UiState {
  uploadProgress?: number;
  newlyRegisteredUser: boolean;
  commChannelVerificationPostponed: boolean;
  pushPermissionPostponed: boolean;
  unsignedInvoicesCheckDone?: boolean;
  tabState: {
    visibility: DocumentVisibilityState;
    focus: boolean;
  };
}

const initialState: UiState = {
  commChannelVerificationPostponed: false,
  pushPermissionPostponed: false,
  newlyRegisteredUser: false,
  tabState: {
    visibility: 'visible',
    focus: true
  }
};

export const uiSlice = createSlice({
  name: 'appUi',
  initialState,
  reducers: {
    setUploadProgress: (
      state: Draft<UiState>,
      action: PayloadAction<number | undefined>
    ) => {
      state.uploadProgress = action.payload;
    },

    setCommChannelVerificationPostponed: (
      state: Draft<UiState>,
      action: PayloadAction<boolean>
    ) => {
      state.commChannelVerificationPostponed = action.payload;
    },

    setPushPermissionPostponed: (
      state: Draft<UiState>,
      action: PayloadAction<boolean>
    ) => {
      state.pushPermissionPostponed = action.payload;
    },

    setUnsignedInvoicesCheckDone: (state: Draft<UiState>) => {
      state.unsignedInvoicesCheckDone = true;
    },

    updateTabState: (
      state,
      action: PayloadAction<Partial<UiState['tabState']>>
    ) => {
      state.tabState = {
        ...state.tabState,
        ...action.payload
      };
    }
  },
  extraReducers: (builder) => {
    builder.addCase(register.fulfilled, (state) => {
      state.newlyRegisteredUser = true;
    });

    const isUserAuthenticated = isFulfilled(login, pingCurrentUser);
    builder.addMatcher(isUserAuthenticated, (state, action) => {
      state.commChannelVerificationPostponed = isPostponedOnLocalStorage(
        action.payload.id,
        POSTPONE_VERIFICATION_STORAGE_KEY,
        POSTPONE_VERIFICATION_TTL
      );
      state.pushPermissionPostponed = isPostponedOnLocalStorage(
        action.payload.id,
        POSTPONE_PUSH_STORAGE_KEY,
        POSTPONE_PUSH_TTL
      );
    });
  }
});

export const {
  setUploadProgress,
  setUnsignedInvoicesCheckDone,
  updateTabState
} = uiSlice.actions;
export const { reducer: appUiReducer } = uiSlice;
